/*
  AoA count-up (loaded with defer by the sections that use it).
  Markup: the server renders the final value, <span data-aoa-counter="160" aria-hidden="true">160</span>,
  next to a static copy for screen readers. This script only animates it:
  - Once per element, 0 -> value in 0.9s ease-out (design system), when it is 40% visible.
  - An element already on screen when it is first observed keeps its value: no flash.
  - The width is held while counting, so the text around it does not move.
  - Nothing with prefers-reduced-motion or without IntersectionObserver: the value stays as rendered.
  No listeners on window, no MutationObserver; in the theme editor it re-scans on shopify:section:load
  and stops observing on shopify:section:unload.
*/
(function () {
  'use strict';

  if (window.aoaCounter) return;
  window.aoaCounter = true;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;

  var SELECTOR = '[data-aoa-counter]';
  var DURATION = 900;
  var THRESHOLD = 0.4;
  var state = new WeakMap();

  function parse(element) {
    var raw = (element.getAttribute('data-aoa-counter') || '').trim();
    var value = parseFloat(raw);
    if (!isFinite(value)) return null;
    return { value: value, decimals: (raw.split('.')[1] || '').length, text: element.textContent };
  }

  // Hold the width of the final text, then show the start value in the same task (no paint between)
  function hold(element, counter) {
    element.style.minWidth = '';
    element.textContent = counter.text;
    element.style.minWidth = element.getBoundingClientRect().width + 'px';
    element.textContent = (0).toFixed(counter.decimals);
  }

  function release(element, counter) {
    element.textContent = counter.text;
    element.style.minWidth = '';
  }

  function run(element, counter) {
    hold(element, counter);
    var start = 0;
    function frame(now) {
      if (!element.isConnected) return;
      if (!start) start = now;
      var progress = Math.min(1, (now - start) / DURATION);
      if (progress < 1) {
        var eased = 1 - Math.pow(1 - progress, 3);
        element.textContent = (counter.value * eased).toFixed(counter.decimals);
        window.requestAnimationFrame(frame);
      } else {
        release(element, counter);
      }
    }
    window.requestAnimationFrame(frame);
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var element = entry.target;
      var counter = state.get(element);
      if (!counter) return;

      if (!counter.seen) {
        counter.seen = true;
        if (entry.isIntersecting) {
          // Already visible: keep the final value
          observer.unobserve(element);
          state.delete(element);
          return;
        }
        hold(element, counter);
        return;
      }

      if (entry.intersectionRatio >= THRESHOLD) {
        observer.unobserve(element);
        state.delete(element);
        run(element, counter);
      }
    });
  }, { threshold: [0, THRESHOLD] });

  function watch(scope) {
    scope.querySelectorAll(SELECTOR).forEach(function (element) {
      if (state.has(element)) return;
      var counter = parse(element);
      if (!counter) return;
      state.set(element, counter);
      observer.observe(element);
    });
  }

  // Stop observing what leaves the page, so the observer never keeps removed nodes
  function forget(scope) {
    scope.querySelectorAll(SELECTOR).forEach(function (element) {
      if (!state.has(element)) return;
      observer.unobserve(element);
      state.delete(element);
    });
  }

  watch(document);

  // Theme editor: sections re-render in place
  document.addEventListener('shopify:section:load', function (event) {
    watch(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    forget(event.target);
  });
})();
